import {
  useState,
  useEffect,
  useRef
} from 'react';

import {
  Capacitor,
  CapacitorHttp
} from '@capacitor/core';

import {
  App
} from '@capacitor/app';

import {
  fetchInitialStudentChat,
  fetchStudentChats
} from './ChatContext';

import {
  createClient
} from '@supabase/supabase-js';

const SUPABASE_URL =
  process.env.REACT_APP_SUPABASE_URL;

const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

  const API_URL =
  process.env.REACT_APP_API_URL;

const chatImageCache =
  new Map();

const supabase =
  createClient(
    SUPABASE_URL,
    SUPABASE_KEY
  );

const READ_HEADERS = {
  apikey: SUPABASE_KEY
};

const JSON_HEADERS = {
  apikey: SUPABASE_KEY,
  'Content-Type':
    'application/json'
};

const EMOJI_LIST = [
  '😀','😁','😂','🤣','😃','😄','😅','😆','😉','😊',
  '😋','😎','😍','🥰','😘','😗','😙','😚','🙂','🤗',
  '🤩','🤔','🤨','😐','😑','😶','🙄','😏','😣','😥',
  '😮','🤐','😯','😪','😫','🥱','😴','😌','😛','😜',
  '😝','🤤','😒','😓','😔','😕','🙃','🤑','😲','☹️',
  '🙁','😖','😞','😟','😤','😢','😭','😦','😧','😨',
  '😩','🤯','😬','😰','😱','🥵','🥶','😳','🤪','😵',
  '😡','😠','🤬','😷','🤒','🤕','🤢','🤮','🤧','🥴',
  '👍','👎','👌','✌️','🤞','🤟','🤘','👈','👉','👆',
  '👇','☝️','✋','🤚','🖐','🖖','👋','🤙','💪','🦾',
  '🙌','👏','🤲','🤝','🙏','❤️','🧡','💛','💚','💙',
  '💜','🖤','🤍','🤎','💔','❣️','💕','💞','💓','💗',
  '💖','💘','💝','💟','☮️','✝️','☯️','🔥','💫','⭐',
  '🌟','✨','⚡','🎉','🎊','🎈','🎁','🏆','🥇','💯'
];

async function parseResponse(
  response,
  label
) {
  let data;

  try {
    data = await response.json();
  } catch (err) {
    data = null;
  }

  if (!response.ok) {
    console.error(
      `${label} failed:`,
      response.status,
      data
    );

    throw new Error(
      data?.message ||
      data?.error ||
      `${label} failed with HTTP ${response.status}`
    );
  }

  return data;
}

async function fetchMessages(chatId) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/chat_messages?chat_id=eq.${chatId}&order=created_at.asc&select=*,students(student_code,english_name,chinese_name)`,
    {
      headers: READ_HEADERS
    }
  );

  return parseResponse(
    response,
    'Fetching messages'
  );
}

async function sendMessage(
  chatId,
  studentId,
  content,
  fileUrl,
  fileName,
  fileType,
  replyToMessageId = null
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/chat_messages`,
    {
      method: 'POST',
      headers: {
        ...JSON_HEADERS,
        Prefer:
          'return=representation'
      },
      body: JSON.stringify({
        chat_id: chatId,
        student_id: studentId,
        content:
          content || null,
        file_url:
          fileUrl || null,
        file_name:
          fileName || null,
        file_type:
          fileType || null,
        reply_to_message_id:
          replyToMessageId || null
      })
    }
  );

  const data =
    await parseResponse(
      response,
      'Sending message'
    );

  return data?.[0] || null;
}

// The student_id filter is deliberate.
// Even if this function is accidentally called with another
// student's message ID, this request will not match it.
async function deleteMessage(
  messageId,
  studentId
) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/chat_messages?id=eq.${messageId}&student_id=eq.${studentId}`,
    {
      method: 'PATCH',
      headers: JSON_HEADERS,
      body: JSON.stringify({
        is_deleted: true,
        content: null,
        file_url: null,
        file_name: null
      })
    }
  );

  if (!response.ok) {
    let data = null;

    try {
      data =
        await response.json();
    } catch (err) {
      // No JSON response
    }

    console.error(
      'Deleting message failed:',
      response.status,
      data
    );

    throw new Error(
      data?.message ||
      data?.error ||
      `Deleting message failed with HTTP ${response.status}`
    );
  }
}

async function markMessagesRead(
  messageIds,
  studentId
) {
  if (!messageIds.length) return;

  const rows =
    messageIds.map(id => ({
      message_id: id,
      student_id: studentId
    }));

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/chat_message_reads`,
    {
      method: 'POST',
      headers: {
        ...JSON_HEADERS,
        Prefer:
          'resolution=merge-duplicates'
      },
      body:
        JSON.stringify(rows)
    }
  );

  if (!response.ok) {
    let data = null;

    try {
      data =
        await response.json();
    } catch (err) {
      // No JSON response
    }

    console.error(
      'Marking messages read failed:',
      response.status,
      data
    );
  }
}

async function fetchReadCounts(
  messageIds
) {
  if (!messageIds.length) {
    return {};
  }

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/chat_message_reads?message_id=in.(${messageIds.join(',')})&select=message_id`,
    {
      headers: READ_HEADERS
    }
  );

  const data =
    await parseResponse(
      response,
      'Fetching read counts'
    );

  const counts = {};

  data.forEach(row => {
    counts[row.message_id] =
      (counts[row.message_id] || 0) +
      1;
  });

  return counts;
}

async function fetchStudentInfo(
  studentId
) {
  if (!studentId) return null;

  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/students?id=eq.${studentId}&select=student_code,english_name,chinese_name`,
    {
      headers: READ_HEADERS
    }
  );

  const data =
    await parseResponse(
      response,
      'Fetching student information'
    );

  return data?.[0] || null;
}

async function uploadFile(file, chatLabel, messageId) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const base64Data = reader.result.split(',')[1];
        await fetch(process.env.REACT_APP_API_URL, {
          method: 'POST',
          mode: 'no-cors',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            action: 'uploadChatFile',
            base64Data,
            mimeType: file.type || 'application/octet-stream',
            fileName: file.name,
            subFolder: chatLabel || 'General',
            messageId
          })
        });
        resolve();
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file.'));
    reader.readAsDataURL(file);
  });
}

function getDriveFileId(url) {
  if (!url) return null;
  const match = url.match(/[?&]id=([^&]+)/) || url.match(/\/d\/([^/?]+)/);
  return match ? match[1] : null;
}

async function loadNativeChatImage(message) {
  const messageId =
    message?.id;

  const fileId =
    getDriveFileId(
      message?.file_url
    );

  if (
    !messageId ||
    !fileId ||
    !API_URL
  ) {
    return null;
  }

  if (
    chatImageCache.has(fileId)
  ) {
    return chatImageCache.get(
      fileId
    );
  }

  const response =
    await CapacitorHttp.post({
      url: API_URL,
      headers: {
        'Content-Type':
          'application/json'
      },
      data: {
        action:
          'getChatImageData',
        messageId
      }
    });

  if (
    response.status < 200 ||
    response.status >= 300
  ) {
    throw new Error(
      `Chat image request failed (${response.status}).`
    );
  }

  const data =
    typeof response.data ===
    'string'
      ? JSON.parse(
          response.data
        )
      : response.data;

  if (!data?.ok) {
    throw new Error(
      data?.error ||
      'Chat image request failed.'
    );
  }

  if (
    !data.hasImage ||
    !data.base64Data
  ) {
    return null;
  }

  const src =
    `data:${
      data.mimeType ||
      'image/jpeg'
    };base64,${
      data.base64Data
    }`;

  chatImageCache.set(
    fileId,
    src
  );

  return src;
}

function ChatMessageImage({
  message
}) {
  const fileId =
    getDriveFileId(
      message?.file_url
    );

  const isNative =
    Capacitor.getPlatform() !==
    'web';

  const browserSrc =
    fileId
      ? `https://drive.google.com/thumbnail?id=${fileId}&sz=w800`
      : null;

  const [
    imageSrc,
    setImageSrc
  ] = useState(() => {
    if (!isNative) {
      return browserSrc;
    }

    return (
      chatImageCache.get(
        fileId
      ) || null
    );
  });

  const [
    imageFailed,
    setImageFailed
  ] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setImageFailed(false);

    if (!fileId) {
      setImageSrc(null);

      return () => {
        cancelled = true;
      };
    }

    if (!isNative) {
      setImageSrc(browserSrc);

      return () => {
        cancelled = true;
      };
    }

    const cached =
      chatImageCache.get(
        fileId
      );

    if (cached) {
      setImageSrc(cached);

      return () => {
        cancelled = true;
      };
    }

    setImageSrc(null);

    loadNativeChatImage(
      message
    )
      .then(src => {
        if (!cancelled) {
          setImageSrc(src);

          if (!src) {
            setImageFailed(true);
          }
        }
      })
      .catch(error => {
        console.error(
          'Chat image load failed:',
          error
        );

        if (!cancelled) {
          setImageSrc(null);
          setImageFailed(true);
        }
      });

    return () => {
      cancelled = true;
    };
    
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [
    message?.id,
    fileId,
    browserSrc,
    isNative
  ]);

  if (
    imageFailed &&
    !imageSrc
  ) {
    return (
      <div
        style={
          styles.pendingFile
        }
      >
        Image unavailable
      </div>
    );
  }

  if (!imageSrc) {
    return (
      <div
        style={
          styles.pendingFile
        }
      >
        Loading image...
      </div>
    );
  }

  return (
    <img
      src={imageSrc}
      alt={
        message?.file_name ||
        'Uploaded image'
      }
      style={
        styles.messageImage
      }
      onClick={() =>
        window.open(
          message.file_url,
          '_blank'
        )
      }
    />
  );
}

function formatTime(dateStr) {
  if (!dateStr) return '';

  const d =
    new Date(dateStr);

  const today =
    new Date();

  const isToday =
    d.toDateString() ===
    today.toDateString();

  if (isToday) {
    return d.toLocaleTimeString(
      'en-GB',
      {
        hour: '2-digit',
        minute: '2-digit'
      }
    );
  }

  return (
    d.toLocaleDateString(
      'en-GB',
      {
        day: 'numeric',
        month: 'short'
      }
    ) +
    ' ' +
    d.toLocaleTimeString(
      'en-GB',
      {
        hour: '2-digit',
        minute: '2-digit'
      }
    )
  );
}

function getStudentName(student) {
  return (
    student?.english_name ||
    student?.chinese_name ||
    student?.student_code ||
    'Unknown'
  );
}

function isImageFile(
  fileType,
  fileName
) {
  if (
    fileType === 'image'
  ) {
    return true;
  }

  if (!fileName) {
    return false;
  }

  return /\.(jpg|jpeg|png|gif|webp)$/i.test(
    fileName
  );
}

function getMessagePreview(message) {
  if (!message) {
    return 'Original message unavailable';
  }

  if (message.is_deleted) {
    return 'Message removed';
  }

  const content = String(
    message.content || ''
  ).trim();

  if (content) {
    return content.length > 120
      ? content.slice(0, 117) + '...'
      : content;
  }

  if (
    message.file_url &&
    isImageFile(
      message.file_type,
      message.file_name
    )
  ) {
    return 'Image: ' +
      (message.file_name || 'Image');
  }

  if (message.file_url) {
    return 'File: ' +
      (message.file_name || 'File');
  }

  return 'Message';
}

export default function Chat({
  session,
  initialCourseId,
  initialChatId,
  onBack
}) {
  const [chats, setChats] =
    useState([]);

  const [
    activeChatId,
    setActiveChatId
  ] = useState(
    initialChatId || null
  );

  const [
    messages,
    setMessages
  ] = useState([]);

  const [
    readCounts,
    setReadCounts
  ] = useState({});

  const [loading, setLoading] =
    useState(true);

  const [
    chatChoicesLoading,
    setChatChoicesLoading
  ] = useState(false);

  const [
    chatChoicesLoaded,
    setChatChoicesLoaded
  ] = useState(false);

  const [sending, setSending] =
    useState(false);

  const [text, setText] =
    useState('');

  const [
    showEmoji,
    setShowEmoji
  ] = useState(false);

  const [showSearch, setShowSearch] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFileBrowser, setShowFileBrowser] = useState(false);

  const [
    uploadingFile,
    setUploadingFile
  ] = useState(false);

  const [error, setError] =
    useState('');

  const [
    hoveredMessageId,
    setHoveredMessageId
  ] = useState(null);

  const [
    replyToMessageId,
    setReplyToMessageId
  ] = useState(null);

  const messagesEndRef =
    useRef(null);

  const fileInputRef =
    useRef(null);

  const textInputRef =
    useRef(null);

  const subscriptionRef =
    useRef(null);

    const activeChatIdRef =
  useRef(activeChatId);

useEffect(() => {
  activeChatIdRef.current =
    activeChatId;
}, [activeChatId]);


/*
 * Grow the message composer as the
 * student types.
 *
 * It expands to roughly six lines,
 * then becomes internally scrollable.
 */
useEffect(() => {
  const input =
    textInputRef.current;

  if (!input) return;

  const maxHeight = 160;

  input.style.height = 'auto';

  const nextHeight =
    Math.min(
      Math.max(
        input.scrollHeight,
        42
      ),
      maxHeight
    );

  input.style.height =
    `${nextHeight}px`;

  input.style.overflowY =
    input.scrollHeight > maxHeight
      ? 'auto'
      : 'hidden';

}, [text]);

  // ------------------------------------------------------------
  // Load available chats
  // ------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;

    async function loadChats() {
      setLoading(true);
      setError('');

      try {
        /*
         * Keep native behaviour unchanged.
         *
         * The native app already presents Chat as its
         * own top-level area, so continue resolving the
         * complete chat list there.
         */
        if (
          Capacitor.getPlatform() !==
          'web'
        ) {
          const result =
            await fetchStudentChats(
              session
            );

          if (cancelled) {
            return;
          }

          setChats(
            result
          );

          setChatChoicesLoaded(
            true
          );

          setActiveChatId(
            current => {
              if (
                current &&
                result.some(
                  chat =>
                    chat.id ===
                    current
                )
              ) {
                return current;
              }

              if (
                initialCourseId
              ) {
                const courseChat =
                  result.find(
                    chat =>
                      chat.courseId ===
                        initialCourseId
                      &&
                      chat.type ===
                        'course'
                  );

                if (courseChat) {
                  return courseChat.id;
                }
              }

              return (
                result.length > 0
                  ? result[0].id
                  : null
              );
            }
          );

          return;
        }


        /*
         * WEB:
         *
         * If the caller already supplied a chat ID,
         * start loading that thread immediately.
         *
         * Do not discover every other chat first.
         */
        if (initialChatId) {
          setChats([
            {
              id:
                initialChatId,

              type:
                'direct',

              label:
                'Current chat'
            }
          ]);

          setActiveChatId(
            initialChatId
          );

          return;
        }


        /*
         * Otherwise resolve one course chat only.
         *
         * With initialCourseId this is that course.
         * Without it, this is one current enabled course.
         */
        const initial =
          await fetchInitialStudentChat(
            session,
            initialCourseId ||
              null
          );

        if (cancelled) {
          return;
        }

        setChats(
          initial
            ? [initial]
            : []
        );

        setActiveChatId(
          initial?.id ||
          null
        );

      } catch (err) {
        console.error(
          'Failed to load initial chat:',
          err
        );

        if (!cancelled) {
          setError(
            err.message ||
            'Failed to load chat.'
          );
        }

      } finally {
        if (!cancelled) {
          setLoading(
            false
          );
        }
      }
    }


    loadChats();


    return () => {
      cancelled =
        true;
    };

  }, [
    session,
    initialCourseId,
    initialChatId
  ]);


  async function loadChatChoices() {
    /*
     * Native already loaded the complete list.
     */
    if (
      Capacitor.getPlatform() !==
        'web'
      ||
      chatChoicesLoaded
      ||
      chatChoicesLoading
    ) {
      return;
    }


    setChatChoicesLoading(
      true
    );


    try {
      const result =
        await fetchStudentChats(
          session
        );


      /*
       * Preserve the already-open thread while replacing
       * placeholder metadata with the full discovered data.
       */
      setChats(
        current => {
          const merged =
            new Map();


          current.forEach(
            chat => {
              merged.set(
                String(
                  chat.id
                ),
                chat
              );
            }
          );


          result.forEach(
            chat => {
              merged.set(
                String(
                  chat.id
                ),
                chat
              );
            }
          );


          return [
            ...merged.values()
          ];
        }
      );


      setChatChoicesLoaded(
        true
      );

    } catch (err) {
      console.error(
        'Failed to load other chats:',
        err
      );

      setError(
        err.message ||
        'Failed to load other chats.'
      );

    } finally {
      setChatChoicesLoading(
        false
      );
    }
  }

  // ------------------------------------------------------------
  // Load active chat + subscribe
  // ------------------------------------------------------------

  useEffect(() => {
    if (!activeChatId) {
      return undefined;
    }

    let cancelled = false;

    setMessages([]);
    setReadCounts({});
    setReplyToMessageId(null);
    setError('');

    async function loadMessages() {
      try {
        const msgs =
          await fetchMessages(
            activeChatId
          );

        if (cancelled) return;

        setMessages(msgs);

        const ids =
          msgs.map(m => m.id);

        try {
          const counts =
            await fetchReadCounts(
              ids
            );

          if (!cancelled) {
            setReadCounts(
              counts
            );
          }
        } catch (err) {
          console.error(
            'Could not load read counts:',
            err
          );
        }

        const unreadOtherMessages =
          msgs
            .filter(
              msg =>
                msg.student_id !==
                session.studentId
            )
            .map(
              msg => msg.id
            );

        if (
          unreadOtherMessages.length
        ) {
          await markMessagesRead(
            unreadOtherMessages,
            session.studentId
          );
        }
      } catch (err) {
        console.error(
          'Failed to load messages:',
          err
        );

        if (!cancelled) {
          setError(
            err.message ||
            'Failed to load messages.'
          );
        }
      }
    }

    loadMessages();

    if (
      subscriptionRef.current
    ) {
      supabase.removeChannel(
        subscriptionRef.current
      );

      subscriptionRef.current =
        null;
    }

    const channel =
      supabase
        .channel(
          `chat-${activeChatId}`
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table:
              'chat_messages',
            filter:
              `chat_id=eq.${activeChatId}`
          },
          async payload => {
            if (
              payload.eventType ===
              'INSERT'
            ) {
              const newMsg =
                payload.new;

              let student = null;

              try {
                student =
                  await fetchStudentInfo(
                    newMsg.student_id
                  );
              } catch (err) {
                console.error(
                  'Could not fetch sender info:',
                  err
                );
              }

              const msgWithStudent = {
                ...newMsg,
                students: student
              };

              setMessages(
                prev => {
                  const alreadyExists =
                    prev.some(
                      msg =>
                        msg.id ===
                        newMsg.id
                    );

                  if (
                    alreadyExists
                  ) {
                    return prev.map(
                      msg =>
                        msg.id ===
                        newMsg.id
                          ? {
                              ...msg,
                              ...msgWithStudent
                            }
                          : msg
                    );
                  }

                  return [
                    ...prev,
                    msgWithStudent
                  ];
                }
              );

              if (
                newMsg.student_id !==
                session.studentId
              ) {
                await markMessagesRead(
                  [newMsg.id],
                  session.studentId
                );
              }
            }

            if (
              payload.eventType ===
              'UPDATE'
            ) {
              setMessages(
                prev =>
                  prev.map(
                    msg =>
                      msg.id ===
                      payload.new.id
                        ? {
                            ...msg,
                            ...payload.new
                          }
                        : msg
                  )
              );
            }
          }
        )
        .subscribe(status => {
          console.log(
            `Realtime chat ${activeChatId}:`,
            status
          );
        });

    subscriptionRef.current =
      channel;

    return () => {
      cancelled = true;

      if (
        subscriptionRef.current
      ) {
        supabase.removeChannel(
          subscriptionRef.current
        );

        subscriptionRef.current =
          null;
      }
    };
  }, [
    activeChatId,
    session.studentId
  ]);

  // ------------------------------------------------------------
// Quietly catch up when the native app returns to the foreground
// ------------------------------------------------------------

useEffect(() => {
  if (
    Capacitor.getPlatform() ===
    'web'
  ) {
    return undefined;
  }

  let listener = null;
  let cancelled = false;

  async function syncOnResume() {
    const chatId =
      activeChatIdRef.current;

    if (!chatId) {
      return;
    }

    try {
      const msgs =
        await fetchMessages(
          chatId
        );

      if (
        cancelled ||
        activeChatIdRef.current !==
          chatId
      ) {
        return;
      }

      setMessages(prev => {
        const previousSignature =
          prev
            .map(msg =>
              [
                msg.id,
                msg.content,
                msg.file_url,
                msg.file_name,
                msg.file_type,
                msg.is_deleted,
                msg.reply_to_message_id
              ].join(':')
            )
            .join('|');

        const nextSignature =
          msgs
            .map(msg =>
              [
                msg.id,
                msg.content,
                msg.file_url,
                msg.file_name,
                msg.file_type,
                msg.is_deleted,
                msg.reply_to_message_id
              ].join(':')
            )
            .join('|');

        return (
          previousSignature ===
          nextSignature
        )
          ? prev
          : msgs;
      });

      const ids =
        msgs.map(
          msg => msg.id
        );

      try {
        const counts =
          await fetchReadCounts(
            ids
          );

        if (
          !cancelled &&
          activeChatIdRef.current ===
            chatId
        ) {
          setReadCounts(
            counts
          );
        }
      } catch (err) {
        console.error(
          'Could not refresh chat read counts:',
          err
        );
      }

      const unreadOtherMessages =
        msgs
          .filter(
            msg =>
              msg.student_id !==
              session.studentId
          )
          .map(
            msg => msg.id
          );

      if (
        unreadOtherMessages.length
      ) {
        await markMessagesRead(
          unreadOtherMessages,
          session.studentId
        );
      }
    } catch (err) {
      // Returning to the app should never replace the chat
      // with an error screen just because a background refresh failed.
      console.error(
        'Could not refresh chat after app resume:',
        err
      );
    }
  }

  async function attachListener() {
    listener =
      await App.addListener(
        'appStateChange',
        ({
          isActive
        }) => {
          if (
            isActive &&
            !cancelled
          ) {
            syncOnResume();
          }
        }
      );
  }

  attachListener();

  return () => {
    cancelled = true;

    if (listener) {
      listener.remove();
    }
  };
}, [session.studentId]);

  // ------------------------------------------------------------
  // Scroll to newest message
  // ------------------------------------------------------------

  useEffect(() => {
    messagesEndRef.current
      ?.scrollIntoView({
        behavior: 'smooth'
      });
  }, [messages]);

  function addCreatedMessage(
    message
  ) {
    if (!message) return;

    setMessages(prev => {
      if (
        prev.some(
          existing =>
            existing.id ===
            message.id
        )
      ) {
        return prev;
      }

      return [
        ...prev,
        {
          ...message,
          students: null
        }
      ];
    });
  }

  // ------------------------------------------------------------
  // Send text
  // ------------------------------------------------------------

  async function handleSend(e) {
    e?.preventDefault();

    if (
      !text.trim() ||
      sending ||
      !activeChatId
    ) {
      return;
    }

    setSending(true);
    setError('');

    const messageText =
      text.trim();

    try {
      const createdMessage =
        await sendMessage(
          activeChatId,
          session.studentId,
          messageText,
          null,
          null,
          null,
          replyToMessageId
        );

      if (!createdMessage) {
        throw new Error(
          'Supabase did not return the new message.'
        );
      }

      addCreatedMessage(
        createdMessage
      );

      setText('');
      setReplyToMessageId(null);
      setShowEmoji(false);
    } catch (err) {
      console.error(
        'Message send failed:',
        err
      );

      setError(
        err.message ||
        'Failed to send message.'
      );
    } finally {
      setSending(false);
    }
  }

  // ------------------------------------------------------------
  // Upload file
  // ------------------------------------------------------------

  async function handleFileUpload(
    file
  ) {
    if (!file) return;

    if (!activeChatId) {
      setError(
        'No active chat selected.'
      );

      return;
    }

    if (
      file.size >
      10 * 1024 * 1024
    ) {
      setError(
        'File too large. Maximum size is 10MB.'
      );

      return;
    }

    setUploadingFile(true);
    setError('');

   try {
      const isImage =
        /\.(jpg|jpeg|png|gif|webp)$/i.test(file.name);

      // Create message immediately with 'pending' URL
      const createdMessage = await sendMessage(
        activeChatId,
        session.studentId,
        null,
        'pending',
        file.name,
        isImage ? 'image' : 'document'
      );

      if (!createdMessage) {
        throw new Error('File uploaded, but the chat message record was not created.');
      }

      addCreatedMessage(createdMessage);
      setReplyToMessageId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setUploadingFile(false);

      // Send file to Drive via Apps Script (no-cors)
      uploadFile(file, activeChat?.label, createdMessage.id);

      // Poll for real URL (Apps Script writes it back to Supabase)
      for (let i = 0; i < 15; i++) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        const checkResponse = await fetch(
          `${SUPABASE_URL}/rest/v1/chat_messages?id=eq.${createdMessage.id}&select=file_url`,
          { headers: { 'apikey': SUPABASE_KEY, 'Authorization': 'Bearer ' + SUPABASE_KEY } }
        );
        const checkData = await checkResponse.json();
        if (checkData[0]?.file_url && checkData[0].file_url !== 'pending') break;
      }

    } catch (err) {
      console.error('File upload/send failed:', err);
      setError(err.message || 'Failed to upload file.');
      setUploadingFile(false);
    }
  }
  // ------------------------------------------------------------
  // Unsend
  // ------------------------------------------------------------

  async function handleUnsend(
    messageId
  ) {
    const target =
      messages.find(
        msg =>
          msg.id === messageId
      );

    // Never allow this handler to operate on somebody else's
    // message.
    if (
      !target ||
      target.student_id !==
        session.studentId
    ) {
      console.warn(
        'Blocked attempt to unsend another student’s message.'
      );

      return;
    }

    if (
      !window.confirm(
        'Remove this message?'
      )
    ) {
      return;
    }

    setError('');

    try {
      await deleteMessage(
        messageId,
        session.studentId
      );

      setMessages(
        prev =>
          prev.map(
            msg =>
              msg.id ===
              messageId
                ? {
                    ...msg,
                    is_deleted: true,
                    content: null,
                    file_url: null,
                    file_name: null
                  }
                : msg
          )
      );
    } catch (err) {
      console.error(
        'Unsend failed:',
        err
      );

      setError(
        err.message ||
        'Failed to remove message.'
      );
    }
  }

  function handleReply(message) {
    if (!message || message.is_deleted) {
      return;
    }

    setReplyToMessageId(
      message.id
    );
    setShowEmoji(false);

    window.requestAnimationFrame(
      () => {
        textInputRef.current
          ?.focus();
      }
    );
  }

  function scrollToMessage(messageId) {
    const element =
      document.getElementById(
        'chat-message-' + messageId
      );

    element?.scrollIntoView({
      behavior: 'smooth',
      block: 'center'
    });
  }

  function handleKeyDown(e) {
    if (
      e.key === 'Enter' &&
      !e.shiftKey
    ) {
      e.preventDefault();
      handleSend();
    }
  }

  const activeChat =
    chats.find(
      chat =>
        chat.id ===
        activeChatId
    );

  const replyingToMessage =
    replyToMessageId
      ? messages.find(
          msg =>
            msg.id ===
            replyToMessageId
        ) || null
      : null;

  if (
    activeChatId &&
    chats.length > 0 &&
    !activeChat
  ) {
    console.warn(
      'Active chat ID is not present in chat list:',
      activeChatId
    );
  }

  if (loading) {
    return (
      <div style={styles.wrap}>
        <div style={styles.muted}>
          Loading...
        </div>
      </div>
    );
  }

  if (chats.length === 0) {
    return (
      <div style={styles.wrap}>

        <div
          style={
            styles.standaloneHeader
          }
        >
          {onBack && (
            <button
              style={
                styles.backBtn
              }
              onClick={onBack}
            >
              ← Back
            </button>
          )}

          <div
            style={
              styles.headerTitle
            }
          >
            Chat
          </div>
        </div>

        <div style={styles.content}>
          {error ? (
            <div style={styles.error}>
              {error}
            </div>
          ) : (
            <div style={styles.muted}>
              No chats available yet.
            </div>
          )}
        </div>

      </div>
    );
  }

  return (
    <div style={styles.wrap}>

      {/* Chat selector */}
      <div style={styles.chatHeader}>

        {onBack && (
          <button
            type="button"
            style={styles.backBtn}
            onClick={onBack}
          >
            ← Back
          </button>
        )}

        <select
          style={styles.chatSelect}
          value={
            activeChatId || ''
          }
          onFocus={
            loadChatChoices
          }
          onMouseDown={
            loadChatChoices
          }
          onChange={e =>
            setActiveChatId(
              e.target.value
            )
          }
        >
          {chats.map(
            chat => (
              <option
                key={chat.id}
                value={chat.id}
              >
                {chat.label}
              </option>
            )
          )}

          {chatChoicesLoading && (
            <option
              value="__loading__"
              disabled
            >
              Loading other chats...
            </option>
          )}
        </select>

      </div>

<div style={styles.headerTools}>
          <button
            type="button"
            style={{ ...styles.iconBtn, ...(showSearch ? styles.iconBtnActive : {}) }}
            onClick={() => { setShowSearch(prev => !prev); setSearchTerm(''); }}
            title="Search messages"
          >Search</button>
          <button
            type="button"
            style={{ ...styles.iconBtn, ...(showFileBrowser ? styles.iconBtnActive : {}) }}
            onClick={() => setShowFileBrowser(prev => !prev)}
            title="Browse files"
          >Files</button>
        </div>

      {/* Search bar */}
      {showSearch && (
        <div style={styles.searchBar}>
          <input
            autoFocus
            style={styles.searchInput}
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Search messages..."
          />
          {searchTerm && (
            <button style={styles.searchClear} onClick={() => setSearchTerm('')}>✕</button>
          )}
        </div>
      )}

      {/* File browser panel */}
      {showFileBrowser && (
        <div style={styles.fileBrowser}>
          <div style={styles.fileBrowserHeader}>
            <span style={styles.fileBrowserTitle}>Files</span>
            <button style={styles.fileBrowserClose} onClick={() => setShowFileBrowser(false)}>✕</button>
          </div>
          <div style={styles.fileBrowserList}>
            {messages
              .filter(m => m.file_url && m.file_url !== 'pending' && m.file_type !== 'image')
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
              .map(m => (
                <div key={m.id} style={styles.fileBrowserItem}>
                  <div style={styles.fileBrowserName}>{m.file_name || 'File'}</div>
                  <div style={styles.fileBrowserDate}>
                    {new Date(m.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                  <div style={styles.fileBrowserActions}>
                    
                     <a href={`https://drive.google.com/file/d/${getDriveFileId(m.file_url)}/preview`}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.fileBrowserBtn}
                    >View</a>
                    
                     <a href={`https://drive.google.com/uc?export=download&id=${getDriveFileId(m.file_url)}`}
                      target="_blank"
                      rel="noreferrer"
                      style={styles.fileBrowserBtn}
                    >Download</a>
                  </div>
                </div>
              ))}
            {messages.filter(m => m.file_url && m.file_url !== 'pending' && m.file_type !== 'image').length === 0 && (
              <div style={styles.fileBrowserEmpty}>No files uploaded yet.</div>
            )}
          </div>
        </div>
      )}

      {/* Messages */}
      <div
        style={
          styles.messagesArea
        }
      >
       {messages
          .filter(msg =>
            !searchTerm.trim() ||
            (msg.content && msg.content.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (msg.file_name && msg.file_name.toLowerCase().includes(searchTerm.toLowerCase()))
          )
          .map(msg => {
          const isOwn =
            msg.student_id ===
            session.studentId;

          const isDeleted =
            msg.is_deleted;

          const readCount =
            readCounts[msg.id] ||
            0;

          const isHovered =
            hoveredMessageId ===
            msg.id;

          const repliedToMessage =
            msg.reply_to_message_id
              ? messages.find(
                  candidate =>
                    candidate.id ===
                    msg.reply_to_message_id
                ) || null
              : null;

          return (
            <div
              key={msg.id}
              id={
                'chat-message-' +
                msg.id
              }
              style={{
                ...styles.messageRow,
                ...(isOwn
                  ? styles.messageRowOwn
                  : {})
              }}
              onMouseEnter={() =>
                setHoveredMessageId(
                  msg.id
                )
              }
              onMouseLeave={() =>
                setHoveredMessageId(
                  null
                )
              }
            >

              {!isOwn && (
                <div
                  style={
                    styles.senderName
                  }
                >
                  {getStudentName(
                    msg.students
                  )}
                </div>
              )}

              <div
                style={{
                  ...styles.bubble,
                  ...(isOwn
                    ? styles.bubbleOwn
                    : styles.bubbleOther)
                }}
              >
                {isDeleted ? (
                  <div
                    style={
                      styles.deletedText
                    }
                  >
                    Message removed
                  </div>
                ) : (
                  <>
                    {msg.reply_to_message_id && (
                      <button
                        type="button"
                        style={
                          styles.replyQuote
                        }
                        disabled={
                          !repliedToMessage
                        }
                        onClick={() =>
                          repliedToMessage &&
                          scrollToMessage(
                            repliedToMessage.id
                          )
                        }
                        title={
                          repliedToMessage
                            ? 'Go to original message'
                            : 'Original message unavailable'
                        }
                      >
                        <div
                          style={
                            styles.replyQuoteAuthor
                          }
                        >
                          {repliedToMessage
                            ? repliedToMessage.student_id === session.studentId
                              ? 'You'
                              : getStudentName(
                                  repliedToMessage.students
                                )
                            : 'Original message'}
                        </div>

                        <div
                          style={
                            styles.replyQuoteText
                          }
                        >
                          {getMessagePreview(
                            repliedToMessage
                          )}
                        </div>
                      </button>
                    )}

                    {msg.content && (
                      <div
                        style={
                          styles.messageText
                        }
                      >
                        {msg.content}
                      </div>
                    )}

                    {msg.file_url && msg.file_url !== 'pending' &&
  isImageFile(
    msg.file_type,
    msg.file_name
  ) && (
    <div>
      <ChatMessageImage
        message={msg}
      />

      <div
        style={
          styles.attachmentActions
        }
      >
                            <a
                              href={`https://drive.google.com/uc?export=download&id=${getDriveFileId(msg.file_url)}`}
                              target="_blank"
                              rel="noreferrer"
                              style={
                                styles.attachmentButton
                              }
                            >
                              Download
                            </a>
                          </div>
                        </div>
                        
                      )}

                    {msg.file_url && msg.file_url !== 'pending' &&
                      !isImageFile(
                        msg.file_type,
                        msg.file_name
                      ) && (
                        <div
                          style={
                            styles.fileAttachment
                          }
                        >
                          <div
                            style={
                              styles.fileHeader
                            }
                          >
                            <span
                              style={
                                styles.fileIcon
                              }
                            >
                              FILE
                            </span>

                            <div
                              style={
                                styles.fileName
                              }
                            >
                              {msg.file_name ||
                                'File'}
                            </div>
                          </div>

                          <div
                            style={
                              styles.attachmentActions
                            }
                          >
                            <a
                              href={`https://drive.google.com/file/d/${getDriveFileId(msg.file_url)}/preview`}
                              target="_blank"
                              rel="noreferrer"
                              style={
                                styles.attachmentButton
                              }
                            >
                              View
                            </a>

                            
                              <a href={`https://drive.google.com/uc?export=download&id=${getDriveFileId(msg.file_url)}`}
                              target="_blank"
                              rel="noreferrer"
                              style={
                                styles.attachmentButton
                              }
                            >
                              Download
                            </a>
                          </div>
                        </div>
                      )}
                  </>
                  
                )}
              </div>

                    {msg.file_url === 'pending' && (
                      <div style={styles.pendingFile}>
                        ⏳ {msg.file_name} — uploading...
                      </div>
                    )}

              <div
                style={
                  styles.messageMeta
                }
              >
                <span
                  style={
                    styles.messageTime
                  }
                >
                  {formatTime(
                    msg.created_at
                  )}
                </span>

                {readCount > 0 && (
                  <span
                    style={
                      styles.readCount
                    }
                  >
                    Seen by{' '}
                    {readCount}
                  </span>
                )}

                {!isDeleted && (
                  <button
                    type="button"
                    style={styles.replyBtn}
                    onClick={() =>
                      handleReply(msg)
                    }
                  >
                    Reply
                  </button>
                )}

                {isOwn &&
                  !isDeleted &&
                  isHovered && (
                    <button
                      style={
                        styles.unsendBtn
                      }
                      onClick={() =>
                        handleUnsend(
                          msg.id
                        )
                      }
                    >
                      Unsend
                    </button>
                  )}
              </div>
            </div>
          );
        })}

        <div
          ref={
            messagesEndRef
          }
        />
      </div>

      {/* Input area */}
      <div
        style={styles.inputArea}
      >

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        {replyingToMessage && (
          <div
            style={
              styles.replyComposer
            }
          >
            <button
              type="button"
              style={
                styles.replyComposerBody
              }
              onClick={() =>
                scrollToMessage(
                  replyingToMessage.id
                )
              }
              title="Go to original message"
            >
              <div
                style={
                  styles.replyComposerLabel
                }
              >
                Replying to{' '}
                {replyingToMessage.student_id === session.studentId
                  ? 'yourself'
                  : getStudentName(
                      replyingToMessage.students
                    )}
              </div>

              <div
                style={
                  styles.replyComposerText
                }
              >
                {getMessagePreview(
                  replyingToMessage
                )}
              </div>
            </button>

            <button
              type="button"
              style={
                styles.replyCancelBtn
              }
              onClick={() =>
                setReplyToMessageId(null)
              }
              title="Cancel reply"
            >
              ×
            </button>
          </div>
        )}

        {showEmoji && (
          <div
            style={
              styles.emojiPanel
            }
          >
            {EMOJI_LIST.map(
              emoji => (
                <button
                  key={emoji}
                  type="button"
                  style={
                    styles.emojiBtn
                  }
                  onClick={() =>
                    setText(
                      prev =>
                        prev +
                        emoji
                    )
                  }
                >
                  {emoji}
                </button>
              )
            )}
          </div>
        )}

        <div
          style={styles.inputRow}
        >

          <button
            type="button"
            style={styles.iconBtn}
            onClick={() =>
              setShowEmoji(
                prev => !prev
              )
            }
            title="Emoji"
          >
            Emoji
          </button>

          <button
            type="button"
            style={styles.iconBtn}
            onClick={() =>
              fileInputRef.current
                ?.click()
            }
            disabled={
              uploadingFile ||
              !activeChatId
            }
            title="Upload file"
          >
            Attach
          </button>

          <input
            type="file"
            ref={fileInputRef}
            style={{
              display: 'none'
            }}
            onChange={e =>
              handleFileUpload(
                e.target.files?.[0]
              )
            }
          />

          <textarea
            ref={textInputRef}
            style={
              styles.textInput
            }
            value={text}
            onChange={e =>
              setText(
                e.target.value
              )
            }
            onKeyDown={
              handleKeyDown
            }
            placeholder={
              uploadingFile
                ? 'Uploading...'
                : 'Type a message... (Enter to send, Shift+Enter for new line)'
            }
            rows={1}
            disabled={
              uploadingFile
            }
          />

          <button
            type="button"
            style={styles.sendBtn}
            onClick={handleSend}
            disabled={
              sending ||
              uploadingFile ||
              !text.trim() ||
              !activeChatId
            }
          >
            {sending
              ? '...'
              : '→'}
          </button>

        </div>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    width: 'min(100%, 680px)',
    height: '100%',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    background: '#f7f8fa',
    minHeight: 'calc(100vh - 136px)'
  },

  content: {
    padding: '20px'
  },

  muted: {
    color: '#627d98',
    fontSize: '14px',
    padding: '20px'
  },

standaloneHeader: {
  padding:
    'calc(14px + var(--vm-safe-top, env(safe-area-inset-top, 0px))) 18px 14px',
  background: '#102a43',
  borderBottom:
    '1px solid #163752',
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  flexShrink: 0
},

  headerTitle: {
    fontSize: '16px',
    fontWeight: '700',
    color: 'white'
  },

  chatHeader: {
    padding:
      '14px 18px 0',
    background:
      '#f7f8fa',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    position: 'sticky',
    top: 0,
    zIndex: 10
  },

  backBtn: {
    border:
      '1px solid #d9e2ec',
    background: 'white',
    borderRadius: '5px',
    padding: '8px 11px',
    fontSize: '13px',
    fontWeight: '600',
    color: '#243b53',
    cursor: 'pointer',
    flexShrink: 0
  },

  chatSelect: {
    flex: 1,
    minWidth: 0,
    border:
      '1px solid #bcccdc',
    borderRadius: '8px',
    padding: '10px 12px',
    fontSize: '13px',
    fontWeight: '800',
    background: 'white',
    color: '#486581'
  },

  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    padding: '14px 18px 150px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },

  messageRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '2px',
    maxWidth: '84%'
  },

  messageRowOwn: {
    alignItems: 'flex-end'
  },

  senderName: {
    fontSize: '10px',
    fontWeight: '700',
    color: '#829ab1',
    margin:
      '0 4px 4px',
    paddingLeft: 0
  },

  bubble: {
    maxWidth: '100%',
    borderRadius:
      '12px 12px 12px 4px',
    padding:
      '10px 12px',
    fontSize: '14px',
    lineHeight: '1.45',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere'
  },

  bubbleOwn: {
    background:
      '#eaf2f8',
    color:
      '#243b53',
    border:
      '1px solid #c9ddeb',
    borderRadius:
      '12px 12px 4px 12px'
  },

  bubbleOther: {
    background:
      'white',
    color:
      '#243b53',
    border:
      '1px solid #d9e2ec',
    borderRadius:
      '12px 12px 12px 4px'
  },

  deletedText: {
    color: '#aaa',
    fontStyle: 'italic',
    fontSize: '13px'
  },

  messageText: {
    whiteSpace: 'pre-wrap'
  },

  messageImage: {
    maxWidth: '100%',
    maxHeight: '300px',
    borderRadius: '5px',
    display: 'block',
    cursor: 'pointer',
    marginBottom: '6px'
  },

  fileAttachment: {
    minWidth: '180px'
  },

  fileHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '7px'
  },

  fileIcon: {
    fontSize: '9px',
    fontWeight: '800',
    letterSpacing: '0.06em',
    flexShrink: 0
  },

  fileName: {
    fontSize: '13px',
    fontWeight: '600',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },

  attachmentActions: {
    display: 'flex',
    gap: '5px',
    alignItems: 'center'
  },

  attachmentButton: {
    display: 'inline-block',
    padding: '2px 7px',
    borderRadius: '4px',
    border:
      '1px solid currentColor',
    color: 'inherit',
    background:
      'rgba(255,255,255,0.12)',
    textDecoration: 'none',
    fontSize: '10px',
    fontWeight: '600',
    lineHeight: '16px',
    cursor: 'pointer'
  },

  messageMeta: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    margin:
      '4px 4px 0',
    color:
      '#829ab1'
  },

  messageTime: {
    fontSize: '10px',
    color: '#829ab1'
  },

  readCount: {
    fontSize: '10px',
    color: '#829ab1'
  },

  replyBtn: {
    border: 'none',
    background: 'none',
    color: '#627d98',
    fontSize: '11px',
    cursor: 'pointer',
    padding: 0,
    textDecoration:
      'underline'
  },

  unsendBtn: {
    border: 'none',
    background: 'none',
    color: '#a12a2a',
    fontSize: '11px',
    cursor: 'pointer',
    padding: 0,
    textDecoration:
      'underline'
  },

  replyQuote: {
    width: '100%',
    display: 'block',
    textAlign: 'left',
    border: 'none',
    borderLeft:
      '3px solid currentColor',
    borderRadius: '6px',
    background:
      'rgba(127,127,127,0.12)',
    color: 'inherit',
    padding: '6px 8px',
    marginBottom: '7px',
    cursor: 'pointer',
    fontFamily: 'inherit'
  },

  replyQuoteAuthor: {
    fontSize: '11px',
    fontWeight: '700',
    opacity: 0.85,
    marginBottom: '2px'
  },

  replyQuoteText: {
    fontSize: '12px',
    lineHeight: '1.35',
    opacity: 0.8,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },

  inputArea: {
    flexShrink: 0,
    background: 'white',
    borderTop:
      '1px solid #d9e2ec',
    padding:
      '10px 12px',
    boxShadow:
      '0 -2px 8px rgba(36,59,83,0.04)'
  },

  replyComposer: {
    display: 'flex',
    alignItems: 'stretch',
    gap: '8px',
    border:
      '1px solid #d9e2ec',
    borderLeft:
      '4px solid #2f6f9f',
    borderRadius: '5px',
    background: '#f7f8fa',
    marginBottom: '8px',
    overflow: 'hidden'
  },

  replyComposerBody: {
    flex: 1,
    minWidth: 0,
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    padding: '8px 10px',
    cursor: 'pointer',
    fontFamily: 'inherit'
  },

  replyComposerLabel: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#2f6f9f',
    marginBottom: '2px'
  },

  replyComposerText: {
    fontSize: '12px',
    color: '#627d98',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },

  replyCancelBtn: {
    width: '36px',
    border: 'none',
    background: 'transparent',
    color: '#627d98',
    fontSize: '22px',
    cursor: 'pointer',
    flexShrink: 0
  },

  emojiPanel: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    padding: '8px',
    background: '#f7f8fa',
    borderRadius: '6px',
    border:
      '1px solid #d9e2ec',
    marginBottom: '8px'
  },

  emojiBtn: {
    border: 'none',
    background: 'none',
    fontSize: '22px',
    cursor: 'pointer',
    padding: '4px',
    borderRadius: '6px',
    lineHeight: 1
  },

  inputRow: {
    display: 'flex',
    gap: '8px',
    alignItems: 'flex-end'
  },

  iconBtn: {
    minWidth: '54px',
    padding: '0 10px',
    border:
      '1px solid #bcccdc',
    background: 'white',
    borderRadius: '8px',
    height: '44px',
    fontSize: '11px',
    fontWeight: '800',
    color: '#486581',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'center',
    flexShrink: 0
  },

  textInput: {
    flex: 1,
    minWidth: 0,
    minHeight: '44px',
    maxHeight: '160px',
    border:
      '1px solid #bcccdc',
    borderRadius: '8px',
    padding:
      '10px 11px',
    fontSize: '14px',
    resize: 'none',
    fontFamily: 'inherit',
    lineHeight: '1.4',
    overflowY: 'hidden',
    boxSizing: 'border-box',
    background: 'white',
    color: '#243b53'
  },

  sendBtn: {
    width: '44px',
    background: '#243b53',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    height: '44px',
    fontSize: '11px',
    fontWeight: '700',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent:
      'center',
    flexShrink: 0
  },


  headerTools: {
    display: 'flex',
    gap: '6px',
    alignItems: 'center',
    flexShrink: 0
  },

  iconBtnActive: {
    background: '#eef5fa',
    borderColor: '#9fb3c8',
    color: '#2f6f9f'
  },

  searchBar: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    padding: '9px 16px',
    background: 'white',
    borderBottom: '1px solid #d9e2ec'
  },

  searchInput: {
    flex: 1,
    minWidth: 0,
    border: '1px solid #bcccdc',
    borderRadius: '5px',
    padding: '8px 10px',
    fontSize: '13px',
    fontFamily: 'inherit'
  },

  searchClear: {
    width: '32px',
    height: '32px',
    border: '1px solid #d9e2ec',
    background: 'white',
    color: '#627d98',
    borderRadius: '5px',
    cursor: 'pointer'
  },

  fileBrowser: {
    background: 'white',
    borderBottom: '1px solid #d9e2ec',
    maxHeight: '280px',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column'
  },

  fileBrowserHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '11px 16px',
    borderBottom: '1px solid #edf1f5'
  },

  fileBrowserTitle: {
    fontSize: '12px',
    fontWeight: '800',
    color: '#243b53',
    textTransform: 'uppercase',
    letterSpacing: '0.06em'
  },

  fileBrowserClose: {
    border: 'none',
    background: 'transparent',
    color: '#627d98',
    fontSize: '18px',
    cursor: 'pointer'
  },

  fileBrowserList: {
    overflowY: 'auto',
    padding: '4px 16px 10px'
  },

  fileBrowserItem: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) auto auto',
    gap: '12px',
    alignItems: 'center',
    padding: '10px 0',
    borderBottom: '1px solid #edf1f5'
  },

  fileBrowserName: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '13px',
    fontWeight: '600',
    color: '#243b53'
  },

  fileBrowserDate: {
    fontSize: '11px',
    color: '#829ab1',
    whiteSpace: 'nowrap'
  },

  fileBrowserActions: {
    display: 'flex',
    gap: '6px'
  },

  fileBrowserBtn: {
    border: '1px solid #bcccdc',
    background: 'white',
    color: '#2f6f9f',
    borderRadius: '4px',
    padding: '5px 8px',
    fontSize: '10px',
    fontWeight: '700',
    textDecoration: 'none'
  },

  fileBrowserEmpty: {
    padding: '16px 0',
    color: '#829ab1',
    fontSize: '13px'
  },

  error: {
    background: '#faeaea',
    border:
      '1px solid #e8bcbc',
    borderRadius: '5px',
    padding: '8px 12px',
    color: '#a12a2a',
    fontSize: '13px',
    marginBottom: '8px'
  },
  pendingFile: { fontSize: '13px', color: '#627d98', fontStyle: 'italic', marginTop: '4px' },
};